import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Ticket, Users, Calendar, Clock, Building2, Star, Mail, User2, Briefcase, Check, AlertCircle, Trophy, Network, Globe } from 'lucide-react';
import emailjs from '@emailjs/browser';

interface PurchaseFormData {
  first_name: string;
  last_name: string;
  email: string;
  title: string;
  company: string;
  company_address: string;
  vat_number: string;
  registration_number: string;
  number_of_tickets: string;
  terms_accepted: boolean;
}

const TOTAL_TICKETS = 450;

const Tickets = () => {
  const [formData, setFormData] = useState<PurchaseFormData>({
    first_name: '',
    last_name: '',
    email: '',
    title: '',
    company: '',
    company_address: '',
    vat_number: '',
    registration_number: '',
    number_of_tickets: '',
    terms_accepted: false
  });

  const [status, setStatus] = useState<{
    type: 'idle' | 'loading' | 'success' | 'error';
    message?: string;
  }>({ type: 'idle' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.terms_accepted) {
      setStatus({
        type: 'error',
        message: 'Please accept the terms and conditions to proceed.'
      });
      return;
    }

    const numTickets = parseInt(formData.number_of_tickets);
    if (numTickets > TOTAL_TICKETS) {
      setStatus({
        type: 'error',
        message: `Sorry, only ${TOTAL_TICKETS} tickets remaining. Please reduce your quantity.`
      });
      return;
    }

    setStatus({ type: 'loading' });

    try {
      await emailjs.send(
        'service_jna8jdw',
        'template_hf4opyh',
        {
          ...formData,
          to_email: 'symone@titc.io'
        },
        'CEm23wDYlAqPYWh5Q'
      );

      setStatus({
        type: 'success',
        message: 'Your ticket purchase request has been submitted! We will contact you shortly with payment details.'
      });

      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        title: '',
        company: '',
        company_address: '',
        vat_number: '',
        registration_number: '',
        number_of_tickets: '',
        terms_accepted: false
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Failed to submit purchase request. Please try again.'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden" style={{ backgroundColor: '#1A1A1A' }}>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-5xl font-bold mb-6" style={{ color: '#C1B3A3' }}>
              Purchase Your Tickets
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Join South Africa's most exclusive FinTech gathering
            </p>
            <div className="inline-block bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8">
              <div className="mb-4">
                <p className="text-white text-lg mb-2">Individual Ticket Price</p>
                <p className="text-4xl font-bold" style={{ color: '#C1B3A3' }}>R9,750 + VAT</p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-2 text-yellow-400">
              <AlertCircle className="h-5 w-5" />
              <p className="text-sm">Limited availability - Book now to secure your spot</p>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Ticket Availability Banner */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-xl p-6 mb-12 text-white text-center"
        >
          <h2 className="text-2xl font-bold mb-2">Limited Tickets to Purchase Available</h2>
          <p className="text-lg">
            450 tickets have already been reserved and allocated, only 150 tickets are on general sale
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Event Value Proposition */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="p-8">
                <h2 className="text-2xl font-bold mb-6">Why Attend?</h2>
                
                <div className="space-y-8">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <Network className="h-8 w-8 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Elite Networking</h3>
                      <p className="text-gray-600">
                        Connect with 500+ industry leaders, including C-suite executives, founders, and investors 
                        from South Africa's top financial institutions and FinTech companies.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <Trophy className="h-8 w-8 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Exclusive Experience</h3>
                      <p className="text-gray-600">
                        Enjoy a premium evening featuring a champagne reception, gourmet dining, and 
                        entertainment at one of Cape Town's most prestigious venues.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <Globe className="h-8 w-8 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Industry Recognition</h3>
                      <p className="text-gray-600">
                        Be part of South Africa's premier FinTech event, celebrating excellence and 
                        innovation in financial technology.
                      </p>
                    </div>
                  </div>

                  <div className="border-t pt-6 mt-8">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <Calendar className="h-6 w-6 text-gray-400 inline-block mr-2" />
                        <span className="font-medium">November 25th 2025</span>
                      </div>
                      <div>
                        <Clock className="h-6 w-6 text-gray-400 inline-block mr-2" />
                        <span className="font-medium">18:00 - Late</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Building2 className="h-6 w-6 text-gray-400" />
                      <span className="font-medium">Maslow Hotel, Time Square, Pretoria</span>
                    </div>
                  </div>

                  <div className="bg-indigo-50 rounded-lg p-4">
                    <div className="flex items-center space-x-3 text-indigo-600">
                      <Mail className="h-5 w-5" />
                      <span className="font-medium">Need help?</span>
                    </div>
                    <p className="text-gray-600 mt-2">
                      Contact <a href="mailto:symone@titc.io" className="text-indigo-600 hover:text-indigo-800">symone@titc.io</a> for 
                      assistance with your booking.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Purchase Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="p-8">
                <h2 className="text-2xl font-bold mb-4">Purchase Tickets</h2>
                <p className="text-gray-600 mb-6">
                  Please fill in all required fields to complete your ticket purchase.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="first_name"
                        required
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="last_name"
                        required
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                      Job Title *
                    </label>
                    <input
                      type="text"
                      id="title"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      id="company"
                      required
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="company_address" className="block text-sm font-medium text-gray-700">
                      Company Address *
                    </label>
                    <textarea
                      id="company_address"
                      required
                      rows={3}
                      value={formData.company_address}
                      onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="vat_number" className="block text-sm font-medium text-gray-700">
                      VAT Number *
                    </label>
                    <input
                      type="text"
                      id="vat_number"
                      required
                      value={formData.vat_number}
                      onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="registration_number" className="block text-sm font-medium text-gray-700">
                      Registration Number *
                    </label>
                    <input
                      type="text"
                      id="registration_number"
                      required
                      value={formData.registration_number}
                      onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="number_of_tickets" className="block text-sm font-medium text-gray-700">
                      Number of Tickets *
                    </label>
                    <input
                      type="number"
                      id="number_of_tickets"
                      required
                      min="1"
                      max={TOTAL_TICKETS}
                      value={formData.number_of_tickets}
                      onChange={(e) => setFormData({ ...formData, number_of_tickets: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={formData.terms_accepted}
                        onChange={(e) => setFormData({ ...formData, terms_accepted: e.target.checked })}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label htmlFor="terms" className="text-sm text-gray-600">
                        I accept the{' '}
                        <a
                          href="/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-800"
                        >
                          terms and conditions
                        </a>
                      </label>
                    </div>
                    
                    <p className="text-sm text-gray-500 italic">
                      After submitting your purchase request, our team will contact you with payment details
                      and additional information about the event.
                    </p>
                  </div>

                  {status.message && (
                    <div className={`text-sm ${
                      status.type === 'success' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {status.message}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status.type === 'loading'}
                    className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {status.type === 'loading' ? 'Processing...' : 'Purchase Tickets'}
                  </button>
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Tickets;